import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { DestinationDTOWithIcon } from '../../../../components/destinations-details/destination.model';
import { DestinationService } from '../../../../shared/services/destinations.service';
import { catchError, take, tap } from 'rxjs';
import { withDevtools } from '@pc/store';

interface IDestinationsState {
  destinations: DestinationDTOWithIcon[];
  popularDestinations: DestinationDTOWithIcon[];
  pickedDestinations: number[];
  loading: boolean;
  isDomestic: boolean;
  error: string;
}

const initialState: IDestinationsState = {
  destinations: [],
  popularDestinations: [],
  pickedDestinations: [],
  loading: false,
  isDomestic: false,
  error: ''
};

export const DestinationsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('DestinationsStore'),
  withState(initialState),
  withComputed((store) => ({
    pickedDestinationsData: computed<DestinationDTOWithIcon[]>(() =>
      store.pickedDestinations().reduce((acc, pickedId) => {
        const destination = store.destinations().find((data) => data.id === pickedId);
        if (destination) acc.push(destination);
        return acc;
      }, [] as DestinationDTOWithIcon[])
    ),
    unpickedDestinationsData: computed(() =>
      store.destinations().filter((dest) => !store.pickedDestinations().includes(dest.id))
    ),
    unselectedPopularDestinations: computed<DestinationDTOWithIcon[]>(() =>
      store.popularDestinations().filter((destination) => !store.pickedDestinations().includes(destination.id))
    )
  })),
  withMethods((store) => ({
    setDestinations(destinations: DestinationDTOWithIcon[]): void {
      patchState(store, {
        destinations,
        popularDestinations: destinations
          .filter((destination) => destination.popularityOrder > 0)
          .sort((a, b) => a.popularityOrder - b.popularityOrder),
        loading: false
      });
    },
    setPopularDestinations(popularDestinations: DestinationDTOWithIcon[]): void {
      patchState(store, { popularDestinations: popularDestinations });
    },
    setPickedDestinations(pickedDestinationsVal: number[]): void {
      patchState(store, { pickedDestinations: [...pickedDestinationsVal] });
    },
    setisDomestic(isDomestic: boolean): void {
      patchState(store, { isDomestic: isDomestic });
    },
    addDestination(destinationId: number): void {
      patchState(store, { pickedDestinations: [...store.pickedDestinations(), destinationId] });
    }
  })),
  withHooks({
    onInit: (store, destinationsService = inject(DestinationService)) => {
      patchState(store, { loading: true, error: '' });
      destinationsService
        .getDestinations()
        .pipe(
          take(1),
          tap((destinations: DestinationDTOWithIcon[]) => {
            store.setDestinations(destinations);
          }),
          catchError((error) => {
            patchState(store, { error: error as string, loading: false });
            throw error;
          })
        )
        .subscribe();
    }
  })
);
